import React, { useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { api } from "../lib/brand";

const SectionLabel = ({ children }) => <span className="haven-label">{children}</span>;

export default function Welcome() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [status, setStatus] = useState({ phase: "checking", payment_status: null, amount: null, tier: null, email: null });
  const attemptsRef = useRef(0);
  const timerRef = useRef(null);

  useEffect(() => {
    if (!sessionId) {
      setStatus((s) => ({ ...s, phase: "missing" }));
      return;
    }

    const poll = async () => {
      try {
        const { data } = await api.get(`/checkout/status/${sessionId}`);
        if (data.payment_status === "paid") {
          setStatus({
            phase: "paid",
            payment_status: data.payment_status,
            amount: data.amount_total,
            tier: data.tier,
            email: data.email,
          });
          return;
        }
        if (data.status === "expired") {
          setStatus((s) => ({ ...s, phase: "expired" }));
          return;
        }
      } catch {
        // ignore, keep polling
      }
      attemptsRef.current += 1;
      if (attemptsRef.current >= 8) {
        setStatus((s) => ({ ...s, phase: "timeout" }));
        return;
      }
      timerRef.current = setTimeout(poll, 2200);
    };

    poll();
    return () => timerRef.current && clearTimeout(timerRef.current);
  }, [sessionId]);

  return (
    <main data-testid="welcome-page" className="haven-page-shell" style={{ paddingTop: 40 }}>
      {status.phase === "missing" && (
        <div className="haven-card" style={{ padding: 28 }} data-testid="welcome-missing">
          <SectionLabel>No session</SectionLabel>
          <h1 className="font-serif mt-3" style={{ fontSize: 32, color: "var(--ink)", lineHeight: 1.1 }}>
            We couldn't find your checkout session.
          </h1>
          <p style={{ marginTop: 8, color: "var(--ink-soft)", fontSize: 14, lineHeight: 1.6 }}>
            If you completed payment, please check your email for confirmation. Otherwise:
          </p>
          <Link to="/join" className="haven-btn haven-btn-primary mt-4" data-testid="welcome-back-join">
            Back to founding
          </Link>
        </div>
      )}

      {status.phase === "checking" && (
        <div className="haven-card haven-card-gold fade-up" style={{ padding: 28 }} data-testid="welcome-checking">
          <SectionLabel>Confirming…</SectionLabel>
          <h1 className="font-serif mt-3" style={{ fontSize: 32, color: "var(--ink)", lineHeight: 1.1 }}>
            One moment, locking your rate.
          </h1>
          <p style={{ marginTop: 8, color: "var(--ink-soft)", fontSize: 14, lineHeight: 1.6 }}>
            We're confirming the payment with Stripe and creating your founding member record. This usually takes a few seconds.
          </p>
          <div className="mt-5" style={{ height: 6, borderRadius: 999, background: "var(--gold-light)", overflow: "hidden" }}>
            <div
              style={{
                width: "60%", height: "100%", background: "var(--gold)",
                animation: "havenLoadingBar 1400ms ease-in-out infinite alternate",
              }}
            />
          </div>
        </div>
      )}

      {status.phase === "paid" && (
        <div className="haven-card fade-up" style={{ padding: 32, background: "var(--ink)", color: "var(--warm-white)", borderColor: "var(--ink)" }} data-testid="welcome-paid">
          <SectionLabel>Welcome, Founder.</SectionLabel>
          <h1 className="font-serif mt-3" style={{ fontSize: "clamp(36px, 5vw, 56px)", lineHeight: 1.05, color: "var(--warm-white)" }}>
            Your rate is locked.
          </h1>
          <p style={{ marginTop: 12, color: "#D5C9B5", fontSize: 15, lineHeight: 1.7, maxWidth: 580 }}>
            Confirmation is on its way to <b style={{ color: "var(--gold-light)" }}>{status.email || "your inbox"}</b>. Daniel personally onboards every founding member, so expect a hand-written follow-up within 48 hours.
          </p>
          <div className="mt-7 grid sm:grid-cols-3 gap-4" style={{ color: "var(--warm-white)" }}>
            <div>
              <div className="haven-label" style={{ color: "var(--gold-light)" }}>Tier</div>
              <div className="font-serif mt-1" style={{ fontSize: 22 }}>{(status.tier || "monthly").replace(/^\w/, (c) => c.toUpperCase())}</div>
            </div>
            <div>
              <div className="haven-label" style={{ color: "var(--gold-light)" }}>Charged</div>
              <div className="font-serif mt-1" style={{ fontSize: 22 }}>${(status.amount / 100).toFixed(2)}</div>
            </div>
            <div>
              <div className="haven-label" style={{ color: "var(--gold-light)" }}>Status</div>
              <div className="font-serif mt-1" style={{ fontSize: 22 }}>Founding · Locked</div>
            </div>
          </div>
          <div className="mt-7">
            <a href="https://thesewinghaven.com" data-testid="welcome-open-app" className="haven-btn haven-btn-gold">
              Open The Haven →
            </a>
          </div>
        </div>
      )}

      {(status.phase === "timeout" || status.phase === "expired") && (
        <div className="haven-card" style={{ padding: 28 }} data-testid="welcome-timeout">
          <SectionLabel>Hmm.</SectionLabel>
          <h1 className="font-serif mt-3" style={{ fontSize: 32, color: "var(--ink)", lineHeight: 1.1 }}>
            We're still confirming with Stripe.
          </h1>
          <p style={{ marginTop: 8, color: "var(--ink-soft)", fontSize: 14, lineHeight: 1.6 }}>
            Most charges show up here within 30 seconds, but occasionally Stripe takes a minute. Check your email for the receipt — if you got it, you're locked in. Otherwise, please reach out and we'll fix it personally.
          </p>
          <div className="mt-5 flex gap-3">
            <button onClick={() => window.location.reload()} className="haven-btn haven-btn-primary" data-testid="welcome-retry">Recheck</button>
            <Link to="/" className="haven-btn haven-btn-ghost">Back home</Link>
          </div>
        </div>
      )}

      <style>{`
        @keyframes havenLoadingBar {
          0% { transform: translateX(-40%); }
          100% { transform: translateX(40%); }
        }
      `}</style>
    </main>
  );
}
